import React, { useState, useEffect } from 'react';
import { supabase, checkSupabaseConnection } from '../../lib/supabase';
import { AlertCircle, CheckCircle, Info, ArrowRight, RefreshCw, Link as LinkIcon, Database, Shield, ExternalLink, LogOut } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const Troubleshoot: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [supabaseUrl, setSupabaseUrl] = useState<string | null>(null);
  const [supabaseKey, setSupabaseKey] = useState<boolean>(false);
  const [session, setSession] = useState<any>(null);
  const [authResponse, setAuthResponse] = useState<any>(null);
  const [dbResponse, setDbResponse] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [browserInfo, setBrowserInfo] = useState<any>({});
  const [localStorageItems, setLocalStorageItems] = useState<any>({});
  const navigate = useNavigate();

  useEffect(() => {
    // Collect browser information
    setBrowserInfo({
      userAgent: navigator.userAgent,
      cookiesEnabled: navigator.cookieEnabled,
      localStorage: typeof localStorage !== 'undefined',
      sessionStorage: typeof sessionStorage !== 'undefined',
      protocol: window.location.protocol,
      host: window.location.host
    });

    // Collect localStorage items related to auth
    const authItems: Record<string, string> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('auth') || key.includes('supabase'))) {
        try {
          const value = localStorage.getItem(key);
          authItems[key] = value ? (value.length > 50 ? value.substring(0, 50) + '...' : value) : '';
        } catch (e) {
          authItems[key] = 'Error reading value';
        }
      }
    }
    setLocalStorageItems(authItems);

    checkSupabaseConnection();
  }, []);

  async function checkSupabaseConnection() {
    try {
      setLoading(true);
      setError(null);
      
      // Check environment variables
      const url = import.meta.env.VITE_SUPABASE_URL;
      const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      setSupabaseUrl(url || null);
      setSupabaseKey(!!key);
      
      if (!url || !key) {
        setError('Missing Supabase environment variables');
        setLoading(false);
        return;
      }
      
      // Check auth connection
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      setSession(sessionData);
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        setError(`Auth connection error: ${sessionError.message}`);
        setLoading(false);
        return;
      }
      
      // If we have a session, try to fetch the user profile
      if (sessionData.session?.user) {
        try {
          const { data: profileData, error: profileError } = await supabase
            .from('users')
            .select(`
              *,
              organizations:user_organizations(
                id,
                organization:organizations(*),
                role:user_roles(*)
              )
            `)
            .eq('id', sessionData.session.user.id)
            .single();
            
          if (profileError) {
            console.error('Profile fetch error:', profileError);
            setUserProfile({ error: profileError });
          } else {
            setUserProfile(profileData);
          }
        } catch (err) {
          console.error('Profile fetch error:', err);
          setUserProfile({ error: err });
        }
      }
      
      // Try a simple auth operation (without actually sending an email)
      try {
        const { error: authError } = await supabase.auth.signInWithOtp({
          email: 'test@example.com',
          options: {
            shouldCreateUser: false
          }
        });
        
        setAuthResponse({
          success: !authError,
          message: authError ? authError.message : 'Auth API working correctly',
          error: authError
        });
      } catch (authErr: any) {
        console.error('Auth test error:', authErr);
        setAuthResponse({
          success: false,
          message: authErr instanceof Error ? authErr.message : 'Unknown auth error',
          error: authErr
        });
      }
      
      // Try a simple database query
      try {
        const { data: dbData, error: dbError } = await supabase
          .from('users')
          .select('count')
          .limit(1);
        
        setDbResponse({
          success: !dbError,
          message: dbError ? dbError.message : 'Database connection working correctly',
          error: dbError,
          data: dbData
        });
      } catch (dbErr: any) {
        console.error('Database test error:', dbErr);
        setDbResponse({
          success: false,
          message: dbErr instanceof Error ? dbErr.message : 'Unknown database error',
          error: dbErr
        });
      }
      
    } catch (err: any) {
      console.error('Troubleshooting error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  const handleRefresh = () => {
    setRefreshing(true);
    checkSupabaseConnection();
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      localStorage.removeItem('advisorgrow.auth.token');
      localStorage.removeItem('supabase.auth.token');
      document.cookie = 'advisorgrow-auth=; Max-Age=0; path=/; domain=' + window.location.hostname;
      
      toast.success('Signed out successfully');
      navigate('/auth/login');
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error('Failed to sign out');
    }
  };

  const getStatusColor = (isSuccess: boolean) => {
    return isSuccess ? 'text-green-600' : 'text-red-600';
  };

  const getStatusIcon = (isSuccess: boolean) => {
    return isSuccess ? (
      <CheckCircle className="h-5 w-5 text-green-500" />
    ) : (
      <AlertCircle className="h-5 w-5 text-red-500" />
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Auth Troubleshooting</h1>
          <p className="mt-2 text-gray-600">Diagnosing Supabase connection issues</p>
          
          <div className="mt-4 flex justify-center space-x-4">
            <button 
              onClick={handleRefresh}
              disabled={loading || refreshing}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              {refreshing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Tests
                </>
              )}
            </button>
            
            {session?.session && (
              <button 
                onClick={handleSignOut}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </button>
            )}
          </div>
        </div>
        
        {loading ? (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
            <p className="text-center mt-4 text-gray-600">Running diagnostics...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <div className="flex items-center">
              <AlertCircle className="h-6 w-6 text-red-500 mr-3" />
              <h2 className="text-lg font-medium text-red-800">Connection Error</h2>
            </div>
            <p className="mt-2 text-red-700">{error}</p>
            
            {error.includes('environment variables') && (
              <div className="mt-4 bg-white p-4 rounded-md border border-red-100">
                <h3 className="font-medium text-red-800 mb-2">Missing Supabase environment variables</h3>
                <p className="text-sm text-gray-700 mb-2">
                  Your application is missing the required Supabase configuration. Create a <code>.env</code> file in your project root with the following:
                </p>
                <pre className="bg-gray-50 p-3 rounded text-sm overflow-x-auto">
                  VITE_SUPABASE_URL=https://your-project-id.supabase.co{'\n'}
                  VITE_SUPABASE_ANON_KEY=your-anon-key
                </pre>
                <p className="text-sm text-gray-700 mt-2">
                  You can find these values in your Supabase project dashboard under Project Settings &gt; API.
                </p>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Environment Variables */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <LinkIcon className="h-5 w-5 text-primary mr-2" />
                Environment Variables
              </h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b pb-2">
                  <span className="font-medium">VITE_SUPABASE_URL</span>
                  {supabaseUrl ? (
                    <span className="text-green-600 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      {supabaseUrl}
                    </span>
                  ) : (
                    <span className="text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      Missing
                    </span>
                  )}
                </div>
                <div className="flex justify-between items-center border-b pb-2">
                  <span className="font-medium">VITE_SUPABASE_ANON_KEY</span>
                  {supabaseKey ? (
                    <span className="text-green-600 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Present (masked for security)
                    </span>
                  ) : (
                    <span className="text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      Missing
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Auth Connection */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Shield className="h-5 w-5 text-primary mr-2" />
                Auth Connection
              </h2>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b pb-2">
                  <span className="font-medium">Session Check</span>
                  {session?.session ? (
                    <span className="text-green-600 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Active session for {session.session.user.email}
                    </span>
                  ) : (
                    <span className="text-yellow-600 flex items-center">
                      <Info className="h-4 w-4 mr-1" />
                      No active session
                    </span>
                  )}
                </div>
                
                <div className="flex justify-between items-center border-b pb-2">
                  <span className="font-medium">Auth API Test</span>
                  {authResponse && (
                    <span className={`flex items-center ${getStatusColor(authResponse.success)}`}>
                      {getStatusIcon(authResponse.success)}
                      <span className="ml-1">{authResponse.success ? 'Working' : 'Error'}</span>
                    </span>
                  )}
                </div>
                
                {authResponse && !authResponse.success && (
                  <div className="bg-red-50 p-3 rounded-md text-sm">
                    <p className="font-medium text-red-800">Error Message:</p>
                    <p className="text-red-700">{authResponse.message}</p>
                    {authResponse.error && (
                      <pre className="mt-2 bg-red-100 p-2 rounded overflow-x-auto text-xs">
                        {JSON.stringify(authResponse.error, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* User Profile */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Shield className="h-5 w-5 text-primary mr-2" />
                User Profile
              </h2>
              
              {session?.session ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b pb-2">
                    <span className="font-medium">Auth User ID</span>
                    <span className="text-gray-600">{session.session.user.id}</span>
                  </div>
                  
                  <div className="flex justify-between items-center border-b pb-2">
                    <span className="font-medium">Email</span>
                    <span className="text-gray-600">{session.session.user.email}</span>
                  </div>
                  
                  <div className="flex justify-between items-center border-b pb-2">
                    <span className="font-medium">Profile in Database</span>
                    {userProfile ? (
                      userProfile.error ? (
                        <span className="text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          Error fetching profile
                        </span>
                      ) : (
                        <span className="text-green-600 flex items-center">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Found
                        </span>
                      )
                    ) : (
                      <span className="text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        Not found
                      </span>
                    )}
                  </div>
                  
                  {userProfile && !userProfile.error && (
                    <>
                      <div className="flex justify-between items-center border-b pb-2">
                        <span className="font-medium">Full Name</span>
                        <span className="text-gray-600">{userProfile.full_name || 'Not set'}</span>
                      </div>
                      
                      <div className="flex justify-between items-center border-b pb-2">
                        <span className="font-medium">Organizations</span>
                        <span className="text-gray-600">
                          {userProfile.organizations?.length || 0} connected
                        </span>
                      </div>
                    </>
                  )}
                  
                  {userProfile && userProfile.error && (
                    <div className="bg-red-50 p-3 rounded-md text-sm">
                      <p className="font-medium text-red-800">Profile Error:</p>
                      <p className="text-red-700">{userProfile.error.message || JSON.stringify(userProfile.error)}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  No active session to check profile
                </div>
              )}
            </div>
            
            {/* Database Connection */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Database className="h-5 w-5 text-primary mr-2" />
                Database Connection
              </h2>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b pb-2">
                  <span className="font-medium">Database Query Test</span>
                  {dbResponse && (
                    <span className={`flex items-center ${getStatusColor(dbResponse.success)}`}>
                      {getStatusIcon(dbResponse.success)}
                      <span className="ml-1">{dbResponse.success ? 'Working' : 'Error'}</span>
                    </span>
                  )}
                </div>
                
                {dbResponse && !dbResponse.success && (
                  <div className="bg-red-50 p-3 rounded-md text-sm">
                    <p className="font-medium text-red-800">Error Message:</p>
                    <p className="text-red-700">{dbResponse.message}</p>
                    {dbResponse.error && (
                      <pre className="mt-2 bg-red-100 p-2 rounded overflow-x-auto text-xs">
                        {JSON.stringify(dbResponse.error, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Browser Information */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Info className="h-5 w-5 text-primary mr-2" />
                Browser Environment
              </h2>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center border-b pb-2">
                  <span className="font-medium">Cookies Enabled</span>
                  <span className={browserInfo.cookiesEnabled ? 'text-green-600' : 'text-red-600'}>
                    {browserInfo.cookiesEnabled ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between items-center border-b pb-2">
                  <span className="font-medium">LocalStorage Available</span>
                  <span className={browserInfo.localStorage ? 'text-green-600' : 'text-red-600'}>
                    {browserInfo.localStorage ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between items-center border-b pb-2">
                  <span className="font-medium">Protocol</span>
                  <span className={browserInfo.protocol === 'https:' ? 'text-green-600' : 'text-yellow-600'}>
                    {browserInfo.protocol}
                  </span>
                </div>
                <div className="flex justify-between items-center border-b pb-2">
                  <span className="font-medium">Host</span>
                  <span className="text-gray-600">{browserInfo.host}</span>
                </div>
              </div>
              
              <div className="mt-4">
                <h3 className="font-medium text-gray-700 mb-2">Auth-Related LocalStorage Items:</h3>
                {Object.keys(localStorageItems).length > 0 ? (
                  <div className="bg-gray-50 p-3 rounded-md text-xs overflow-x-auto">
                    <pre>{JSON.stringify(localStorageItems, null, 2)}</pre>
                  </div>
                ) : (
                  <p className="text-yellow-600">No auth-related items found in localStorage</p>
                )}
              </div>
            </div>
            
            {/* Recommendations */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center text-blue-800">
                <Info className="h-5 w-5 text-blue-500 mr-2" />
                Recommendations
              </h2>
              
              <ul className="space-y-3 text-blue-700">
                {!supabaseUrl && (
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>Add <code>VITE_SUPABASE_URL</code> to your <code>.env</code> file</span>
                  </li>
                )}
                
                {!supabaseKey && (
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>Add <code>VITE_SUPABASE_ANON_KEY</code> to your <code>.env</code> file</span>
                  </li>
                )}
                
                {authResponse && !authResponse.success && authResponse.error?.code === "otp_disabled" && (
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>
                      Enable Email OTP in your Supabase project settings:
                      <a 
                        href="https://supabase.com/dashboard/project/_/auth/providers" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline ml-1 inline-flex items-center"
                      >
                        Auth Settings <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </span>
                  </li>
                )}
                
                {session?.session && (!userProfile || userProfile.error) && (
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>
                      <strong>Dashboard Access Issue:</strong> You're authenticated but your user profile is missing or has an error. 
                      This is likely why you can't access the dashboard. Check that the users table exists and has the correct schema.
                    </span>
                  </li>
                )}
                
                {session?.session && userProfile && !userProfile.organizations?.length && (
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>
                      <strong>Missing Organization:</strong> Your user profile doesn't have any organizations. 
                      The dashboard requires at least one organization association.
                    </span>
                  </li>
                )}
                
                {authResponse && !authResponse.success && (
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>Check that your Supabase project is active and the URL is correct</span>
                  </li>
                )}
                
                {dbResponse && !dbResponse.success && (
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>Verify that your database schema matches what the application expects</span>
                  </li>
                )}
                
                {!browserInfo.cookiesEnabled && (
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>Enable cookies in your browser</span>
                  </li>
                )}
                
                {browserInfo.protocol !== 'https:' && (
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>For production, ensure you're using HTTPS</span>
                  </li>
                )}
                
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>Check for CORS issues in the Supabase dashboard</span>
                </li>
                
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>Verify that the required tables exist and have the correct RLS policies</span>
                </li>
              </ul>
              
              <div className="mt-6 flex justify-center space-x-4">
                <Link 
                  to="/auth/login" 
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
                >
                  Return to Login
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                
                {session?.session && (
                  <button
                    onClick={handleSignOut}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Troubleshoot;